// Backend
import React from 'react';

// Frontend
import { Grid, Cell } from 'react-foundation';
import '../../stylesheets/components/navigation/FullQuestions.css';

// Functionality
import { useNavigate } from 'react-router-dom';

// Components
import { Header, ProgressBar, FlashMessage, ErrorRender } from '../../constants/SharedComponents';
import { useDemoContext } from '../../contexts/DemoContext';
import useWindowDimensions from '../../hooks/useWindowDimensions';

export function DemoQuestionnaire() {
  const navigate = useNavigate();
  const { demoData, updateDemoData } = useDemoContext();
  
  // State
  const [flashMessageObject] = React.useState({});
  const [inputs, setInputs] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [error] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [previousPage, setPreviousPage] = React.useState(false);
  const [lastCategory, setLastCategory] = React.useState(false);
  const basicFormButtons = React.useRef([]);

  const { width } = useWindowDimensions();
  
  // Demo questions data - full questionnaire with multiple pages
  const questionOptions = [
    { OptionText: 'Never', OptionScore: 1 },
    { OptionText: 'Seldom', OptionScore: 2 },
    { OptionText: 'Sometimes', OptionScore: 3 },
    { OptionText: 'Often', OptionScore: 4 },
    { OptionText: 'Always', OptionScore: 5 }
  ];

  // All demo questions organized by pages (7 questions per page like the original)
  const allDemoQuestions = [
    // Page 1
    [
      {
        id: 1,
        questionText: 'I am clear about what is expected of me at work',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 2,
        questionText: 'I can decide when to take a break',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 3,
        questionText: 'Different groups at work demand things from me that are hard to combine',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 4,
        questionText: 'I know how to go about getting my job done',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 5,
        questionText: 'I am subject to personal harassment in the form of unkind words or behaviour',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 6,
        questionText: 'I have unachievable deadlines',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 7,
        questionText: 'If work gets difficult, my colleagues will help me',
        questionOptions: JSON.stringify(questionOptions)
      }
    ],
    // Page 2
    [
      {
        id: 8,
        questionText: 'I have to work very intensively',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 9,
        questionText: 'My working time can be flexible',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 10,
        questionText: 'My colleagues are willing to listen to my work-related problems',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 11,
        questionText: 'When changes are made at work, I am clear how they will work out in practice',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 12,
        questionText: 'I have sufficient opportunities to question my manager about changes at work',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 13,
        questionText: 'I receive the respect at work I deserve from my colleagues',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 14,
        questionText: 'Staff are always consulted about change at work',
        questionOptions: JSON.stringify(questionOptions)
      }
    ],
    // Page 3
    [
      {
        id: 15,
        questionText: 'I can rely on my line manager to help me out with a work problem',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 16,
        questionText: 'I get the help and support I need from colleagues',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 17,
        questionText: 'I have unrealistic time pressures',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 18,
        questionText: 'I am pressured to work long hours',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 19,
        questionText: 'I understand how my work fits into the overall aim of the organisation',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 20,
        questionText: 'I am given supportive feedback on the work I do',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 21,
        questionText: 'I have to work very fast',
        questionOptions: JSON.stringify(questionOptions)
      }
    ],
    // Page 4
    [
      {
        id: 22,
        questionText: 'I am subject to bullying at work',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 23,
        questionText: 'I have a choice in deciding how I do my work',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 24,
        questionText: 'Relationships at work are strained',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 25,
        questionText: 'My manager encourages me at work',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 26,
        questionText: 'I have a choice in deciding what I do at work',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 27,
        questionText: 'I have to neglect some tasks because I have too much to do',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 28,
        questionText: 'I am clear what is expected of me at work',
        questionOptions: JSON.stringify(questionOptions)
      }
    ],
    // Page 5
    [
      {
        id: 29,
        questionText: 'There is friction or anger between colleagues',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 30,
        questionText: 'I have a say in my own work speed',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 31,
        questionText: 'I am unable to take sufficient breaks',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 32,
        questionText: 'I understand how my role fits into the overall aim of the organisation',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 33,
        questionText: 'I am told what my duties and responsibilities are',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 34,
        questionText: 'I am able to take regular breaks',
        questionOptions: JSON.stringify(questionOptions)
      },
      {
        id: 35,
        questionText: 'I have sufficient opportunities to question my manager about changes at work',
        questionOptions: JSON.stringify(questionOptions)
      }
    ]
  ];

  const totalPages = allDemoQuestions.length;
  const questionData = allDemoQuestions[currentPage - 1];

  // Set pagination states
  React.useEffect(() => {
    setPreviousPage(currentPage - 1 >= 1 ? currentPage - 1 : null);
    setLastCategory(currentPage === totalPages);
  }, [currentPage, totalPages]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);

    // Store responses for this page
    let storedResponses = JSON.parse(localStorage.getItem('demoQuestionScores') || '{"questionScores": {}}');
    storedResponses.questionScores[currentPage] = inputs;
    localStorage.setItem('demoQuestionScores', JSON.stringify(storedResponses));

    if (!lastCategory) {
      // Move to next page
      setCurrentPage(currentPage + 1);
      setInputs({}); // Clear inputs for next page
      setLoading(false);
    } else {
      // Final submission - go to results
      updateDemoData({
        ...demoData,
        questionnaireResponses: storedResponses.questionScores,
        currentStep: 'questionnaire-complete'
      });

      // Clean up localStorage
      localStorage.removeItem('demoQuestionScores');
      navigate('/demo/categories');
    }
  };

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const previousPageNavigation = () => {
    // Store current responses before going back
    if (Object.keys(inputs).length > 0) {
      let storedResponses = JSON.parse(localStorage.getItem('demoQuestionScores') || '{"questionScores": {}}');
      storedResponses.questionScores[currentPage] = inputs;
      localStorage.setItem('demoQuestionScores', JSON.stringify(storedResponses));
    }
    
    setCurrentPage(currentPage - 1);
    
    // Load previous page responses
    const storedResponses = JSON.parse(localStorage.getItem('demoQuestionScores') || '{"questionScores": {}}');
    const previousResponses = storedResponses.questionScores[currentPage - 1] || {};
    setInputs(previousResponses);
  };

  const pageNavigationClassName = (pageNumber) => {
    let navigationClassName = '';

    if (pageNumber === currentPage) {
      navigationClassName = 'current';
    } else if (pageNumber > currentPage) {
      navigationClassName = 'next';
    } else if (pageNumber < currentPage) {
      navigationClassName = 'previous';
    }

    return navigationClassName;
  };

  // Load stored responses for current page on mount/page change
  React.useEffect(() => {
    const storedResponses = JSON.parse(localStorage.getItem('demoQuestionScores') || '{"questionScores": {}}');
    const currentPageResponses = storedResponses.questionScores[currentPage] || {};
    setInputs(currentPageResponses);
  }, [currentPage]);

  return (
    <>
      <ProgressBar loading={loading} />
      <Header />
      <FlashMessage object={flashMessageObject} columnWrap={true} />
      <ErrorRender object={error} />

      {questionData && (
        <div className="grid-container">
          <Grid className="grid-padding-x align-justify">
            <Cell small={12}>
              <div className="page-content">
                <div className="page-content__header">
                  {previousPage && (
                    <button
                      className="page-content__button--medium page-content__button--back"
                      onClick={previousPageNavigation}
                      id="page-navigation-button--go-back"
                    >
                      Go back
                    </button>
                  )}
                </div>
              </div>

              {/* Pagination indicators */}
              <ul className="question-response-pagination-list">
                {allDemoQuestions.map((page, pageIndex) => (
                  <li
                    className="question-response-pagination-list__item"
                    key={`page-${pageIndex + 1}`}
                  >
                    {pageIndex + 1 === currentPage ? (
                      <span
                        className={`question-response-pagination-list__faux-link question-response-pagination-list__faux-link--${pageNavigationClassName(
                          pageIndex + 1,
                        )}`}
                      >
                        {pageIndex + 1}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className={`question-response-pagination-list__link question-response-pagination-list__link--${pageNavigationClassName(
                          pageIndex + 1,
                        )}`}
                        onClick={() => {
                          if (pageIndex + 1 < currentPage) {
                            // Only allow going back to previous pages
                            setCurrentPage(pageIndex + 1);
                          }
                        }}
                        disabled={pageIndex + 1 > currentPage}
                        id={`page-navigation-button--page-${pageIndex + 1}`}
                      >
                        {pageIndex + 1}
                      </button>
                    )}
                  </li>
                ))}
              </ul>

              {/* Only render the form for all users (demo mode) */}
              <form className="basic-form" onSubmit={handleSubmit}>
                {width >= 1024 && (
                  <>
                    <Grid className="grid-padding-x align-justify">
                      <Cell small={7}></Cell>
                      {questionOptions.map((questionOption, questionOptionIndex) => {
                        return (
                          <Cell
                            small={1}
                            className="text-center basic-form__faux-label"
                            key={questionOption.OptionScore}
                          >
                            <b>{questionOption.OptionText}</b>
                          </Cell>
                        );
                      })}
                    </Grid>
                    {questionData.map((question, questionIndex) => {
                      return (
                        <div
                          className="basic-form__field basic-form__field--row"
                          key={`question-${question.id}`}
                        >
                          <Grid className="grid-padding-x align-justify">
                            <Cell small={7}>
                              <p className="basic-form__faux-label">{question.questionText}</p>
                            </Cell>

                            {JSON.parse(question.questionOptions).map(
                              (questionOption, optionIndex) => {
                                return (
                                  <Cell
                                    small={1}
                                    className="text-center"
                                    key={
                                      'question-' +
                                      question.id +
                                      '-response-' +
                                      questionOption.OptionScore
                                    }
                                  >
                                    <input
                                      type="radio"
                                      name={'question_score[' + question.id + '][option_score]'}
                                      value={questionOption.OptionScore}
                                      onChange={handleChange}
                                      defaultChecked={
                                        inputs[
                                        'question_score[' + question.id + '][option_score]'
                                        ] === questionOption.OptionScore.toString()
                                      }
                                      id={
                                        'util--question-' +
                                        questionIndex +
                                        '-response-' +
                                        optionIndex
                                      }
                                      className="basic-form__radio-button"
                                    />

                                    <label
                                      className="basic-form__label--sr-only"
                                      htmlFor={
                                        'util--question-' +
                                        questionIndex +
                                        '-response-' +
                                        optionIndex
                                      }
                                    >
                                      {questionOption.OptionText}
                                    </label>
                                  </Cell>
                                );
                              },
                            )}
                          </Grid>
                        </div>
                      );
                    })}
                  </>
                )}
                {width < 1024 && (
                  <>
                    {questionData.map((question, questionIndex) => {
                      return (
                        <div
                          className="basic-form__field basic-form__field--row"
                          key={`question-${question.id}`}
                        >
                          <Grid className="grid-padding-x align-justify" style={{ paddingBottom: '15px' }}>
                            <Cell small={3}></Cell>
                            <Cell small={6} className='text-center' style={{ paddingBottom: '15px' }}>
                              <p className="basic-form__faux-label">{question.questionText}</p>
                            </Cell>
                            <Cell small={3}></Cell>

                           <Cell small={12} className="text-center" style={{ display: 'flex', justifyContent: 'center' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                {JSON.parse(question.questionOptions).map((questionOption, optionIndex) => {
                                  return (
                                    <div
                                      key={`question-${question.id}-response-${questionOption.OptionScore}`}
                                      style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
                                    >
                                      <input
                                        type="radio"
                                        name={`question_score[${question.id}][option_score]`}
                                        value={questionOption.OptionScore}
                                        onChange={handleChange}
                                        defaultChecked={
                                          inputs[`question_score[${question.id}][option_score]`] === questionOption.OptionScore.toString()
                                        }
                                        id={`util--question-${questionIndex}-response-${optionIndex}`}
                                        className="basic-form__radio-button"
                                      />
                                      <label
                                        className="basic-form__label"
                                        htmlFor={`util--question-${questionIndex}-response-${optionIndex}`}
                                        style={{ marginLeft: '10px' }}
                                      >
                                        {questionOption.OptionText}
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            </Cell>
                          </Grid>
                        </div>
                      );
                    })}
                  </>
                )}

                <div className="basic-form__actions">
                  {previousPage && (
                    <button
                      type="button"
                      className="basic-form__button basic-form__button--multiple basic-form__button--secondary"
                      onClick={previousPageNavigation}
                      id="page-navigation-button--back"
                    >
                      Back
                    </button>
                  )}

                  <input
                    value={lastCategory === false ? 'Next' : 'Submit'}
                    type="submit"
                    className="basic-form__button"
                    ref={(ref) => (basicFormButtons.current[0] = ref)}
                    id="page-form-button--button"
                  />
                </div>
              </form>
            </Cell>
          </Grid>
        </div>
      )}
    </>
  );
}
